import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { fetchStores, Store, Stores } from './Api/StoresApi';
import { GoogleMap, InfoWindow, Marker, MarkerClusterer, useJsApiLoader } from '@react-google-maps/api';
import { UseLoadScriptOptions } from '@react-google-maps/api/dist/useJsApiLoader';
import { Clusterer } from '@react-google-maps/marker-clusterer';
import { useDebounce } from './Hook/UseDebounce';

import './StoreFinder.scss';

const containerStyle = {
    width: '100%',
    height: 'calc(100vh - 250px)',
};

interface StoreFinderProps {
    onMarkerClick?: (store: Store) => void;
}

declare global {
    interface Window {
        __GGLocale: string;
        dataLayer: any[] | undefined;
        UC_UI?: {
            getServicesBaseInfo: () => Array<{ id: string; consent: { status: boolean } }>;
            showFirstLayer: () => void;
        };
    }
}

function distanceBetween(pointA: google.maps.LatLng, pointB: google.maps.LatLngLiteral): number {
    return google.maps.geometry.spherical.computeDistanceBetween(pointA, new google.maps.LatLng(pointB));
}

function trackSearch(query: string): void {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({
        event: 'store_finder',
        query: query,
    });
}

const jsLoaderConfig: UseLoadScriptOptions = {
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAR1kxLedFvBu5ubE43zZOVM--IC7w-qSU',
    libraries: ['geometry'],
};

function getUsercentricsMapConsentStatus(): boolean | undefined {
    // This is the "Template ID" of the "Google Maps" service in the usercentrics dashboard under "Service Settings".
    const googleMapsTemplateId = 'S1pcEj_jZX';
    return window.UC_UI?.getServicesBaseInfo()?.find((service) => service.id === googleMapsTemplateId)?.consent?.status;
}

export const StoreFinderComponent: FunctionComponent<StoreFinderProps> = (props) => {
    const [googleMapsAllowed, setGoogleMapsAllowed] = useState<boolean | undefined>(getUsercentricsMapConsentStatus());

    useEffect(() => {
        window.addEventListener('UC_UI_INITIALIZED', () => setGoogleMapsAllowed(getUsercentricsMapConsentStatus()));
        window.addEventListener('UC_UI_CMP_EVENT', () => setGoogleMapsAllowed(getUsercentricsMapConsentStatus()));
        window.addEventListener('consent_status', () => setGoogleMapsAllowed(getUsercentricsMapConsentStatus()));
        setGoogleMapsAllowed(getUsercentricsMapConsentStatus());
    }, []);

    if (googleMapsAllowed === undefined) {
        return <></>;
    }

    if (googleMapsAllowed === false) {
        return (
            <div style={{ textAlign: 'center' }}>
                <p>Sie müssen das Laden von Google Maps erlauben um die Filialsuche zu benutzen.</p>
                <button className="button button--primary" onClick={() => window.UC_UI?.showFirstLayer()}>
                    <span>Cookie-Einstellungen öffnen</span>
                </button>
            </div>
        );
    }

    return <AllowedStoreFinderComponent {...props} />;
};

export const AllowedStoreFinderComponent: FunctionComponent<StoreFinderProps> = (props) => {
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [stores, setStores] = useState<Stores>([]);
    const [bounds, setBounds] = useState<(google.maps.LatLngLiteral | google.maps.LatLng)[]>(stores);
    const [filterText, setFilterText] = useState('');
    const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
    const [noStoresFound, setNoStoresFound] = useState(false);

    const { isLoaded } = useJsApiLoader(jsLoaderConfig);

    const locale = window.__GGLocale ?? '';

    const debouncedFilterText = useDebounce(filterText, 500);

    const onLoad = useCallback((map: google.maps.Map) => {
        setMap(map);
        setGeocoder(new google.maps.Geocoder());

        fetchStores(locale).then((stores) => {
            setStores(stores);
            setBounds(stores);
        });
    }, []);

    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    useEffect(() => {
        if (!geocoder) {
            console.warn('Geocoder not found yet, might be set with next re-render (`onLoad` event)');
            return;
        }
        if (debouncedFilterText.trim() === '') {
            setBounds(stores);
            setNoStoresFound(false);
            return;
        }
        trackSearch(debouncedFilterText);

        geocoder.geocode(
            {
                address: debouncedFilterText,
                // From docs (https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingRequests):
                // > The region parameter will only influence, not fully restrict, results from the geocoder.
                // Therefore, if we know that we want to search in AT (because AT postcodes have 4 digits), we set
                // region to AT. Otherwise we use DE, because if the best match for the address is in AT, google will
                // return the AT result anyway (for example "wien", "salzburg").
                region: debouncedFilterText.match(/^[0-9]{4}$/) !== null ? 'at' : 'de',
                language: 'de',
            },
            (results, status) => {
                if (status === 'OK') {
                    const coordinates = results?.at(0)?.geometry.location;

                    if (!coordinates) {
                        throw new Error('No coordinates found');
                    }
                    const withDistance = stores.map((store) => ({
                        store,
                        distance: distanceBetween(coordinates, store),
                    }));
                    const sorted = withDistance.sort((storeA, storeB) => storeA.distance - storeB.distance);
                    const withinRange = sorted.slice(0, 3).filter(({ distance }) => distance < 80000);
                    // If we have stores within 80 km, use those. Otherwise, show only the closest store.
                    if (withinRange.length > 0) {
                        setBounds([coordinates, ...withinRange.map(({ store }) => store)]);
                    } else {
                        setBounds([coordinates, ...sorted.slice(0, 1).map(({ store }) => store)]);
                    }
                    setNoStoresFound(false);
                } else {
                    setNoStoresFound(true);
                    setBounds(stores);
                }
            }
        );
    }, [debouncedFilterText]);

    useEffect(() => {
        // Fit all markers into map
        if (map) {
            const mapBounds = new google.maps.LatLngBounds();
            bounds.forEach((point) => mapBounds.extend(point));
            map.fitBounds(mapBounds);
            if (bounds.length === 1) {
                map.setZoom(15);
            }
        }
    }, [bounds]);

    return (
        <div className="store-finder">
            <label className="store-finder__filter-field">
                Suche
                <input type="text" onChange={(e) => setFilterText(e.currentTarget.value)} placeholder="PLZ / Ort" />
            </label>
            {noStoresFound ? <p>Entsprechend Ihrer Eingabe konnten wir leider kein Fachcentrum finden</p> : null}
            {isLoaded ? (
                <GoogleMap mapContainerStyle={containerStyle} onLoad={onLoad} onUnmount={onUnmount}>
                    <MarkerClusterer>
                        {(clusterer) => (
                            <>
                                {stores.map((store) => (
                                    <StoreMarker
                                        key={store.storeNumber}
                                        map={map}
                                        store={store}
                                        clusterer={clusterer}
                                        onMarkerClick={props.onMarkerClick}
                                    />
                                ))}
                            </>
                        )}
                    </MarkerClusterer>
                </GoogleMap>
            ) : (
                <></>
            )}
        </div>
    );
};

interface StoreProps {
    store: Store;
    clusterer: Clusterer;
    onMarkerClick?: (store: Store) => void;
    map: google.maps.Map | null;
}

const StoreMarker: FunctionComponent<StoreProps> = (props) => {
    const [overlayVisible, setOverlayVisible] = useState(false);

    const position = {
        lat: props.store.lat,
        lng: props.store.lng,
    };

    return (
        <Marker
            key={props.store.lat}
            position={position}
            onClick={() => {
                setOverlayVisible(true);

                if (props.onMarkerClick) {
                    props.onMarkerClick(props.store);
                }
            }}
            clusterer={props.clusterer}
        >
            {overlayVisible ? (
                <InfoWindow onCloseClick={() => setOverlayVisible(false)}>
                    <table className="store-finder__info-table">
                        <tr>
                            <th scope="row">Straße:</th>
                            <td>{props.store.street}</td>
                        </tr>
                        <tr>
                            <th scope="row">PLZ:</th>
                            <td>{props.store.postcode}</td>
                        </tr>
                        <tr>
                            <th scope="row">Stadt:</th>
                            <td>{props.store.city}</td>
                        </tr>
                        <tr>
                            <th scope="row">Telefon:</th>
                            <td>
                                <a href={'tel:' + props.store.phoneNumber}>{props.store.phoneNumber}</a>
                            </td>
                        </tr>
                    </table>
                </InfoWindow>
            ) : null}
        </Marker>
    );
};
